import React, { forwardRef, useEffect, useState } from "react";
import { Col, Popover, Row } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { setRankingScore } from "../../../Redux/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import "../style.css";
import { allPriorities } from "../../../Common/constants";

function Step4() {
  const dispatch = useDispatch();
  const prioritySelections = useSelector(
    (state) => state.filter.prioritySelections
  );
  const rankingScore = useSelector((state) => state.filter.rankingScore);

  const [priorityTags, setPriorityTags] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const Placeholder = forwardRef(
    ({ id, withOpacity, isDragging, style, ...props }, ref) => {
      const inlineStyles = {
        opacity: withOpacity ? "0.5" : "1",
        transformOrigin: "50% 50%",
        height: "120px",
        width: "120px",
        padding: "10px",
        borderRadius: "10px",
        cursor: isDragging ? "grabbing" : "grab",
        backgroundColor: "#ffffff",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: isDragging
          ? "rgb(63 63 68 / 5%) 0px 2px 0px 2px, rgb(34 33 81 / 15%) 0px 2px 3px 2px"
          : "rgb(63 63 68 / 5%) 0px 0px 0px 1px, rgb(34 33 81 / 15%) 0px 1px 3px 0px",
        transform: isDragging ? "scale(1.05)" : "scale(1)",
        ...style,
      };

      return (
        <div ref={ref} style={inlineStyles} {...props}>
          {"Drag to move to the front of others"}
        </div>
      );
    }
  );

  const DraggableTag = ({ priority, ...props }) => {
    const { tag } = props;
    const { listeners, setNodeRef } = useSortable({
      id: tag.id,
    });

    let priorityDataArray = allPriorities.filter(
      (item) => item.value === priority
    );
    let priorityData =
      priorityDataArray.length > 0 ? priorityDataArray[0] : null;

    return (
      priorityData && (
        <Col ref={setNodeRef} {...listeners}>
          <div className="tag-container" value={priorityData.value}>
            <img
              className="tag-image"
              src={priorityData.src}
              alt={priorityData.alt}
            />
            <Popover
              overlayStyle={{
                maxWidth: "20vw",
              }}
              placement="topRight"
              content={priorityData.qContent}
              trigger="hover"
            >
              <span className="pa">
                <QuestionCircleOutlined />
              </span>
            </Popover>
            <div className="tag-label">{priorityData.label}</div>
          </div>
        </Col>
      )
    );
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      setPriorityTags((data) => {
        const oldIndex = data.findIndex((item) => item.id === active.id);
        const newIndex = data.findIndex((item) => item.id === over.id);
        return arrayMove(data, oldIndex, newIndex);
      });
    }
    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  useEffect(() => {
    const selectedSet = new Set(prioritySelections);

    // 1. Keep only valid priorities from existing rankingScore
    const filtered = rankingScore.filter((item) =>
      selectedSet.has(item.priority)
    );

    // 2. Add any new priorities that weren't ranked before
    const missing = prioritySelections.filter(
      (p) => !filtered.find((item) => item.priority === p)
    );

    const merged = [
      ...filtered,
      ...missing.map((p) => ({ priority: p, score: 0 })),
    ];

    // 3. If merged != rankingScore, update Redux
    const hasRankingChanged =
      merged.length !== rankingScore.length ||
      merged.some(
        (item, i) =>
          item.priority !== rankingScore[i]?.priority ||
          item.score !== rankingScore[i]?.score
      );

    if (hasRankingChanged) {
      dispatch(
        setRankingScore(
          merged.map((item, index) => ({
            priority: item.priority,
            score: index === 0 ? 3 : index === 1 ? 2 : index === 2 ? 1 : 0,
          }))
        )
      );
    }

    // 4. Also update priorityTags only if needed
    const updatedTags = merged.map((item, index) => ({
      id: index + 1,
      text: item.priority,
    }));

    const isSame =
      updatedTags.length === priorityTags.length &&
      updatedTags.every((t, i) => t.text === priorityTags[i]?.text);

    if (!isSame) {
      setPriorityTags(updatedTags);
    }
  }, [prioritySelections]);

  useEffect(() => {
    let rankingScoreList = priorityTags.map((item, index) => {
      let score = 0;
      if (index === 0) {
        score = 3;
      } else if (index === 1) {
        score = 2;
      } else if (index === 2) {
        score = 1;
      }
      return {
        priority: item.text,
        score: score,
      };
    });
    dispatch(setRankingScore(rankingScoreList));
  }, [priorityTags]);

  return (
    <>
      <Row className="mt-1">
        {
          <p>
            {prioritySelections.length > 0 ? (
              <b>
                4) Which conservation priorities would you address the most?
                Please drag the top three to the corresponding boxes.
              </b>
            ) : (
              `Please Select Priorities`
            )}
          </p>
        }
      </Row>
      <Row className="mt-1  priority-row ranking-tutorial">
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={priorityTags}
            strategy={horizontalListSortingStrategy}
          >
            {priorityTags.map((item, index) => (
              <>
                {index === 0 && (
                  <Col className="ranking-box ranking-box-first">
                    <b className="ranking-label">1st Priority</b>
                    <DraggableTag
                      priority={item.text}
                      tag={item}
                      key={item.id}
                    />
                  </Col>
                )}
                {index === 1 && (
                  <Col className="ranking-box">
                    <b className="ranking-label">2nd Priority</b>
                    <DraggableTag
                      priority={item.text}
                      tag={item}
                      key={item.id}
                    />
                  </Col>
                )}
                {index === 2 && (
                  <>
                    <Col className="ranking-box">
                      <b className="ranking-label">3rd Priority</b>
                      <DraggableTag
                        priority={item.text}
                        tag={item}
                        key={item.id}
                      />
                    </Col>
                    <Col span={24}>
                      <hr />
                    </Col>
                  </>
                )}
                {index > 2 && (
                  <DraggableTag priority={item.text} tag={item} key={item.id} />
                )}
              </>
            ))}
          </SortableContext>
          <DragOverlay style={{ transformOrigin: "0 0 " }}>
            {activeId ? <Placeholder id={activeId} isDragging /> : null}
          </DragOverlay>
        </DndContext>
      </Row>
      <Row></Row>
    </>
  );
}
export default Step4;
